<template>
    <div>
        <vrm-installation-map
            :location="location"
            :installations="geojson"
            :layers="layers"
            @location="onLocationChanged"
            @bounds="onBoundsChanged"
            @load="onLoaded"
            @layers="onLayersChanged"
            @installation="onInstallationPopupClicked"
        >
            <template v-if="isFiltered" v-slot:filter-indicator>
                <vrm-installations-filter-indicator
                    :count="visibleInstallationCount"
                    :total="totalInstallationCount"
                    @clear="onClearFilters"
                ></vrm-installations-filter-indicator>
            </template>
        </vrm-installation-map>
    </div>
</template>

<style scoped lang="scss"></style>

<script>
import { Vue, Component } from 'vue-property-decorator';
import { AngularDirective } from '@/vue/decorators/angular-interop';
import VrmInstallationMap from '@/vue/components/map/VrmInstallationMap';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';
import { InstallationTank } from '@/vue/helpers/installation/tank';
import VrmInstallationsFilterIndicator from '@/vue/components/shared/VrmInstallationsFilterIndicator.vue';

@Component({
    components: { VrmInstallationsFilterIndicator, VrmInstallationMap },
    emits: ['zoom', 'load', 'location', 'bounds', 'layers', 'installation', 'clear-filters'],
    props: {
        /**
         * List of all installation objects to display.
         */
        installations: {
            type: Array,
            required: true,
            default: () => [],
        },
        /**
         * Object containing the layer control values.
         *
         * runtimeUntilService, tank, warningsOnly.
         */
        layers: {
            type: Object,
            required: false,
            default: () => undefined,
        },
        /**
         * Number of installations that is visible because of applied filters.
         */
        visibleInstallationCount: {
            type: Number,
            required: false,
            default: () => 0,
        },
        /**
         * Total number of installations for the user.
         */
        totalInstallationCount: {
            type: Number,
            required: false,
            default: () => 0,
        },
    },
    data: function() {
        return {
            location: {
                lat: 0,
                lng: 0,
                zoom: 3,
                bearing: 0,
                pitch: 0,
            },
            geojson: null,
        };
    },
    created() {
        this.geojson = this.buildGeoJson(this.installations);
    },
    watch: {
        // eslint-disable-next-line no-unused-vars
        installations(next) {
            this.geojson = this.buildGeoJson(this.installations);
        },
    },
    computed: {
        isFiltered() {
            return this.visibleInstallationCount < this.totalInstallationCount;
        },
    },
    methods: {
        /**
         * Callback for when the "Clear filters" button is pressed.
         *
         * Re-emit the event to the controller.
         */
        onClearFilters() {
            // Add a property to indicate the event is fired from the map.
            // Since the filter indicator is only shown in fullscreen, this should only happen if filters are cleared
            // from fullscreen mode.
            this.$emit('clear-filters', {
                map: true,
            });
        },
        /**
         * Callback when an installation is clicked.
         * @param e
         */
        onInstallationPopupClicked(e) {
            this.$emit('installation', e);
        },
        /**
         * Called when the map location is changed and the user stops moving.
         *
         * E.g. by moving, zooming or pitching.
         *
         * Re-emit the event.
         *
         * @param e
         */
        onLocationChanged(e) {
            this.$emit('location', e);
        },
        /**
         * Called whenever the map bounds change.
         *
         * Re-emit the event.
         *
         * @param e
         */
        onBoundsChanged(e) {
            this.$emit('bounds', e);
        },
        /**
         * Called when mapbox is loaded.
         *
         * Re-emit the event.
         *
         * @param e
         */
        onLoaded(e) {
            this.$emit('load', e);
        },

        onLayersChanged(layers) {
            this.$emit('layers', layers);
        },
        /**
         * Convert a list of installations to a GeoJson object.
         *
         * @param installations
         * @returns {{features: unknown[], crs: {type: string, properties: {name: string}}, type: string}}
         */
        buildGeoJson(installations) {
            return {
                type: 'FeatureCollection',
                crs: { type: 'name', properties: { name: 'urn:ogc:def:crs:OGC:1.3:CRS84' } },
                features: installations.map(this.convertInstallationToGeoJson),
            };
        },
        /**
         * Convert an installation object to a GeoJson Feature object.
         *
         * @param obj
         * @returns {{geometry: {coordinates: (*|string|number|{w: number, x: number, h: number, y: number, i: string}|{w: number, x: number, h: number, y: number, i: string})[], type: string}, type: string, properties}}
         */
        convertInstallationToGeoJson(installation) {
            const roundSecondsToHours = seconds => Math.round((seconds / (60 * 60)) * 10) / 10;

            let serviceCountDown = undefined;
            if (installation.data.gasd) {
                const seconds = parseInt(installation.data.gasd.rawValue) || undefined;

                if (seconds) {
                    // Convert service counter to hours.
                    serviceCountDown = roundSecondsToHours(seconds);
                }
            }

            let serviceInterval = 0;
            if (installation.data.gasi) {
                const seconds = parseInt(installation.data.gasi.rawValue) || undefined;

                if (seconds) {
                    // Convert service counter to hours.
                    serviceInterval = roundSecondsToHours(seconds);
                }
            }

            const { tanks, lowestFuelTank } = this.tanks(installation);

            let lat = 0;
            if (installation.data.lt && installation.data.lt.rawValue) {
                lat = parseFloat(installation.data.lt.rawValue) || 0;
            }

            let lng = 0;
            if (installation.data.lg && installation.data.lg.rawValue) {
                lng = parseFloat(installation.data.lg.rawValue) || 0;
            }

            const serviceWarning = serviceInterval > 0 && serviceCountDown && serviceCountDown <= 0;

            let current_alarms_formatted = installation.current_alarms_formatted || [];

            return {
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [lng, lat],
                },
                properties: {
                    // WARNING: Please keep in mind that the nested properties are converted to strings for individual markers,
                    // and for clusters they automatically get parsed (https://github.com/mapbox/mapbox-gl-js/issues/4380)
                    name: installation.name,
                    idSite: installation.idSite,
                    lastTimestamp: installation.last_timestamp,
                    runningOn: installation.device_icon,
                    alarm: installation.alarm || false,
                    serviceCountDown: serviceCountDown,
                    serviceInterval: serviceInterval,
                    status: installation.status,
                    serviceWarning: serviceWarning,
                    tanks: tanks,
                    lowestFuelTank: lowestFuelTank,
                    current_alarms_formatted: current_alarms_formatted,
                },
            };
        },

        /**
         * Get the tanks for an installation.
         *
         * @param installation
         * @returns {{ tanks , lowestFuelTank }}
         */
        tanks(installation) {
            const { tl, tf, tcn } = installation.data;
            if (!tl || !tl.instances) return {};
            let tanks = {};
            let lowestFuelTank = null;

            for (const key in tl.instances) {
                const tank = new InstallationTank({ tl, tf, tcn }, key);
                tanks[tank.name] = tanks[tank.name] || [];
                tanks[tank.name].push({
                    level: tank.level,
                    warning: tank.hasWarning,
                });
                if (tank.isFuelTank && (!lowestFuelTank || tank.level < lowestFuelTank.level)) {
                    lowestFuelTank = tank;
                }
            }

            // sort tanks of each tankGroup
            for (const key in tanks) {
                tanks[key].sort((tankA, tankB) => tankA.level - tankB.level);
            }
            // sort tankGroups
            tanks = Object.keys(tanks)
                .sort(
                    (tankGroupA, tankGroupB) =>
                        tanks[tankGroupA][0].level - tanks[tankGroupB][0].level,
                )
                .reduce((acc, val) => {
                    acc[val] = tanks[val];
                    return acc;
                }, {});

            return { tanks, lowestFuelTank };
        },
    },
})
@AngularDirective('vrm-overview-map')
@AngularLocaleBundle('installationoverview')
export default class VrmOverviewMap extends Vue {}
</script>
